import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const vertexShader = `
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float uTime;
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vPosition;

  // Simplex-like noise
  vec3 mod289(vec3 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
  }

  void main() {
    vec3 baseColor1 = vec3(0.02, 0.05, 0.15);
    vec3 baseColor2 = vec3(0.05, 0.02, 0.2);
    vec3 accentColor1 = vec3(0.0, 0.6, 1.0);
    vec3 accentColor2 = vec3(0.5, 0.0, 1.0);
    vec3 glowColor = vec3(0.0, 0.94, 1.0);

    float t = uTime * 0.05;
    float n1 = snoise(vec3(vUv * 3.0, t)) * 0.5 + 0.5;
    float n2 = snoise(vec3(vUv * 6.0 + 10.0, t * 0.7)) * 0.5 + 0.5;
    float n3 = snoise(vec3(vUv * 12.0 + 20.0, t * 0.3)) * 0.5 + 0.5;

    vec3 color = mix(baseColor1, baseColor2, n1);
    color = mix(color, accentColor1, n2 * 0.3);
    color = mix(color, accentColor2, n3 * 0.15);

    // Continental patterns
    float continent = smoothstep(0.45, 0.55, n1 * 0.7 + n2 * 0.3);
    color = mix(color, accentColor1 * 0.4, continent * 0.5);

    // Atmosphere rim
    float fresnel = pow(1.0 - abs(dot(vNormal, normalize(-vPosition))), 3.0);
    color += glowColor * fresnel * 0.6;

    // Subtle grid lines
    float gridX = smoothstep(0.98, 1.0, abs(sin(vUv.x * 40.0)));
    float gridY = smoothstep(0.98, 1.0, abs(sin(vUv.y * 20.0)));
    color += glowColor * (gridX + gridY) * 0.05;

    gl_FragColor = vec4(color, 1.0);
  }
`;

export default function Planet({ isZoomed }: { isZoomed: boolean }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const atmosphereRef = useRef<THREE.Mesh>(null);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
    }),
    []
  );

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    uniforms.uTime.value = t;

    if (meshRef.current) {
      meshRef.current.rotation.y = t * 0.03;
    }
    if (atmosphereRef.current) {
      atmosphereRef.current.rotation.y = t * 0.025;
    }
  });

  return (
    <group>
      <mesh ref={meshRef}>
        <sphereGeometry args={[2, 128, 128]} />
        <shaderMaterial
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          uniforms={uniforms}
        />
      </mesh>

      <mesh ref={atmosphereRef} scale={1.02}>
        <sphereGeometry args={[2, 64, 64]} />
        <meshBasicMaterial
          color="#00f0ff"
          transparent
          opacity={0.04}
          side={THREE.BackSide}
        />
      </mesh>

      <mesh scale={1.08}>
        <sphereGeometry args={[2, 64, 64]} />
        <meshBasicMaterial
          color="#4060ff"
          transparent
          opacity={0.02}
          side={THREE.BackSide}
        />
      </mesh>
    </group>
  );
}
